import { useState } from 'react';

import type { CategoryTreeBuildDto } from '@/api/gen/_model/categoryTreeBuildDto';
import { type CategoryTree } from '@/shared/category/type';
import { type CategoryInfoInputType } from '@/views/build/features/category-info';

import { usePublisherCategorySelector } from '../../publisher-category-selector/hooks/usePublisherCategorySelector';
import { type PublisherInfo } from '../../publisher-category-selector/types';
import { useSchoolCategorySelector } from '../../school-category-selector/hooks/useSchoolCategorySelector';
import { useSubjectCategorySelector } from '../../subject-category-selector/hooks/useSubjectCategorySelector';

import { useCategoryActions } from './useCategoryActions';
import { useCategoryReset } from './useCategoryReset';
import { useCategoryValidation } from './useCategoryValidation';

/**
 * Category Selector - 통합 카테고리 상태 관리 hook
 *
 * Composition 패턴을 사용하여 독립적인 hook들을 조합합니다.
 * 각 책임이 분리되어 테스트와 유지보수가 용이합니다.
 *
 * 구성:
 * - useSchoolCategorySelector: 학교 정보 관리
 * - useSubjectCategorySelector: 과목 정보 관리
 * - usePublisherCategorySelector: 출판사 정보 관리
 * - useCategoryReset: Cascading reset (Reducer 패턴)
 * - useCategoryActions: 노드 업데이트 라우팅
 * - useCategoryValidation: 에러 병합 및 검증
 *
 * @example
 * const categoryInfo = useCategorySelector({
 *   formValues,
 *   schoolInfoTree,
 *   subjectTree,
 *   publishers
 * });
 */
export const useCategorySelector = ({
  formValues,
  schoolInfoTree,
  subjectTree,
  publishers,
}: {
  /** 초기값 */
  formValues: CategoryInfoInputType;
  /** BuildInfo의 학교 정보 트리 */
  schoolInfoTree: CategoryTree;
  /** BuildInfo의 과목 트리 */
  subjectTree: CategoryTreeBuildDto[];
  /** 출판사 목록 */
  publishers?: PublisherInfo[];
}) => {
  // Independent state
  const [isLastSchoolInfoSaved, setIsLastSchoolInfoSaved] = useState(
    formValues.isLastSchoolInfoSaved || false,
  );

  // 1. School, Subject, Publisher state hooks
  const schoolInfo = useSchoolCategorySelector({
    formValues,
    schoolInfoTree,
  });

  const subjectInfo = useSubjectCategorySelector({
    subjectFormValues: {
      subject: formValues.subject,
      subSubject: formValues.subSubject,
    },
    subjectTree,
    selectedSchoolInfo: {
      schoolTypeNode: schoolInfo.schoolTypeState.selectedSchoolTypeNode,
      gradeNode: schoolInfo.gradeState.selectedGradeNode,
      semesterNode: schoolInfo.semesterState.selectedSemesterNode,
    },
  });

  const publisherInfo = usePublisherCategorySelector({
    publisherFormValues: {
      publisher: formValues.publisher,
      customPublisher: formValues.customPublisher,
    },
    publishers,
    selectedSchoolGroupId:
      schoolInfo.schoolTypeState.selectedSchoolTypeNode?.groupId,
    selectedGradeId: schoolInfo.gradeState.selectedGradeNode?.nodeId,
    schoolInfoTree,
  });

  // 2. Reset logic (Reducer pattern)
  const resetHandlers = useCategoryReset({
    schoolState: schoolInfo,
    subjectState: subjectInfo,
    publisherState: publisherInfo,
  });

  // 3. Actions (node update routing)
  const actions = useCategoryActions({
    schoolState: schoolInfo,
    subjectState: subjectInfo,
    publisherState: publisherInfo,
    resetHandlers,
  });

  // 4. Validation (error merging)
  const validation = useCategoryValidation({
    schoolState: schoolInfo,
    subjectState: subjectInfo,
    publisherState: publisherInfo,
  });

  // 5. Return value (maintain existing API)
  return {
    // School state
    schoolType: schoolInfo.schoolTypeState,
    grade: schoolInfo.gradeState,
    semester: schoolInfo.semesterState,

    // Subject & Publisher state
    subject: subjectInfo,
    publisher: publisherInfo,

    // Actions
    updateCategoryInfo: actions.updateCategoryInfo,
    updateCustomPublisher: actions.updateCustomPublisher,

    // Validation
    errors: validation.errors,
    validate: validation.validate,

    // Independent state
    isLastSchoolInfoSaved,
    setIsLastSchoolInfoSaved,
  };
};

export type CategorySelectorState = ReturnType<typeof useCategorySelector>;
